// 📁 src/components/account/BillingPage.jsx

import React, { useEffect, useState, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function BillingPage() {
  const [companyName, setCompanyName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [billingCycleFilter, setBillingCycleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [invoiceData, setInvoiceData] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const rowRefs = useRef({});
  const tableRef = useRef(null);

  useEffect(() => {
    const email = localStorage.getItem('email');
    const registry = JSON.parse(localStorage.getItem('accountRegistry') || '{}');
    const account = registry[email] || {};
    setCompanyName(account.companyName || '');
    setTenantId(account.tenantId || '');

    const auditLog = JSON.parse(localStorage.getItem(`paymentAuditLog_${email}`) || '[]');
    setInvoiceData(auditLog);

    const modules = [...new Set(auditLog.map((entry) => entry.moduleName))];
    setAvailableModules(modules);
  }, []);

  const filteredData = invoiceData.filter((entry) => {
    const matchesModule = moduleFilter ? entry.moduleName === moduleFilter : true;
    const matchesCycle = billingCycleFilter ? entry.billingCycle === billingCycleFilter : true;
    const matchesStatus = statusFilter ? entry.paymentStatus === statusFilter : true;

    const purchase = new Date(entry.purchaseDate);
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    const matchesFrom = from ? purchase >= from : true;
    const matchesTo = to ? purchase <= to : true;

    return matchesModule && matchesCycle && matchesStatus && matchesFrom && matchesTo;
  });

  const handleDownloadPDF = async (entry, index) => {
    const ref = rowRefs.current[index];
    if (!ref) return;
    await new Promise((resolve) => setTimeout(resolve, 300));
    const canvas = await html2canvas(ref, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgProps = pdf.getImageProperties(imgData);
    const imgHeight = (imgProps.height * pageWidth) / imgProps.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, imgHeight);
    pdf.save(`invoice_${entry.moduleName}.pdf`);
  };

  const handlePrintTable = () => {
    if (!tableRef.current) return;
    const printWindow = window.open('', '_blank');
    const htmlContent = `
    <html>
      <head>
        <title>Invoice Table Report</title>
        <style>
          body { font-family: sans-serif; padding: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
          th { background-color: #f3f3f3; }
        </style>
      </head>
      <body>
        ${tableRef.current.outerHTML}
      </body>
    </html>
  `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-indigo-700 mb-2">Billing & Invoices</h1>

      {(companyName || tenantId) && (
        <div className="mb-4 text-sm text-gray-600 italic">
          {companyName && <div>Company Name: {companyName}</div>}
          {tenantId && <div>Tenant ID: {tenantId}</div>}
        </div>
      )}

      {/* Filter Row */}
      <div className="flex justify-between flex-wrap gap-4 mb-6">
        {/* Left Filters */}
        <div className="flex gap-4">
          <div>
            <label className="text-sm text-gray-600">Filter by Module</label>
            <select
              className="border px-2 py-1 rounded"
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
            >
              <option value="">Select Module</option>
              {availableModules.map((mod, idx) => (
                <option key={idx} value={mod}>
                  {mod}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Billing Cycle</label>
            <select
              className="border px-2 py-1 rounded"
              value={billingCycleFilter}
              onChange={(e) => setBillingCycleFilter(e.target.value)}
            >
              <option value="">Select Billing Cycle</option>
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Payment Status</label>
            <select
              className="border px-2 py-1 rounded"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Select Status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
        </div>

        {/* Right Filters */}
        <div className="flex gap-4">
          <div>
            <label className="text-sm text-gray-600">From Date</label>
            <input
              type="date"
              className="border px-2 py-1 rounded"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm text-gray-600">To Date</label>
            <input
              type="date"
              className="border px-2 py-1 rounded"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Print Table Button */}
      {filteredData.length > 0 && (
        <div className="mb-4">
          <button
            className="px-4 py-2 bg-indigo-600 text-white rounded shadow hover:bg-indigo-700 text-sm"
            onClick={handlePrintTable}
          >
            Print Report (Filtered Table)
          </button>
        </div>
      )}

      {/* Invoice Table */}
      <div ref={tableRef}>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 text-sm text-gray-700">
              <th className="border px-3 py-2 text-left">Module</th>
              <th className="border px-3 py-2 text-left">Amount</th>
              <th className="border px-3 py-2 text-left">Currency</th>
              <th className="border px-3 py-2 text-left">Status</th>
              <th className="border px-3 py-2 text-left">Purchase Date</th>
              <th className="border px-3 py-2 text-left">Start Date</th>
              <th className="border px-3 py-2 text-left">Expiry Date</th>
              <th className="border px-3 py-2 text-left">Invoice</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-4 text-gray-500 italic">
                  No invoice records found.
                </td>
              </tr>
            ) : (
              filteredData.map((entry, index) => {
                const baseAmount = Number(entry.baseAmount || entry.amount || 0);
                const addOnAmount = Number(entry.addOnAmount || 0);
                const totalAmount = baseAmount + addOnAmount;
                const discountRate = Number(entry.annualDiscountRate || 0);
                const originalAmount =
                  discountRate > 0 ? baseAmount / (1 - discountRate / 100) : baseAmount;
                const discountAmount = discountRate > 0 ? originalAmount - baseAmount : 0;

                return (
                  <tr key={index} className="text-sm text-gray-800">
                    <td className="border px-3 py-2">{entry.moduleName}</td>
                    <td className="border px-3 py-2">{entry.amount}</td>
                    <td className="border px-3 py-2">{entry.currency}</td>
                    <td className="border px-3 py-2">{entry.paymentStatus}</td>
                    <td className="border px-3 py-2">{entry.purchaseDate}</td>
                    <td className="border px-3 py-2">{entry.startDate}</td>
                    <td className="border px-3 py-2">{entry.expiryDate}</td>
                    <td className="border px-3 py-2 space-y-2">
                      <div
                        ref={(el) => (rowRefs.current[index] = el)}
                        className="max-w-3xl p-8 bg-white shadow-lg border border-gray-300"
                        style={{ position: 'absolute', left: '-9999px', top: '0' }}
                      >
                        <div className="flex justify-between items-center mb-6">
                          <div>
                            <h1 className="text-2xl font-bold text-indigo-700">
                              Talentraxion Invoice
                            </h1>
                            <p className="text-sm text-gray-500">
                              Invoice No: {entry.invoiceNo || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-500">
                              Billing Date: {new Date(entry.purchaseDate).toLocaleString('en-BD')}
                            </p>
                          </div>
                          <div className="text-right text-sm text-gray-600">
                            <p>
                              Company: <strong>{companyName}</strong>
                            </p>
                            <p>Tenant ID: {tenantId}</p>
                            <p>Email: {entry.email}</p>
                          </div>
                        </div>

                        <hr className="mb-4" />

                        <div className="mb-4">
                          <h2 className="text-lg font-semibold text-gray-800 mb-2">
                            Subscription Details
                          </h2>
                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                            <p>
                              Module: <strong>{entry.moduleName}</strong>
                            </p>
                            <p>
                              Billing Cycle: <strong>{entry.billingCycle}</strong>
                            </p>
                            <p>
                              Module Period:{' '}
                              <strong>
                                {entry.startDate} → {entry.expiryDate}
                              </strong>
                            </p>
                            <p>
                              Payment Method: <strong>{entry.method}</strong>
                            </p>
                          </div>
                        </div>

                        <div className="mb-4">
                          <h2 className="text-lg font-semibold text-gray-800 mb-2">
                            Amount Breakdown
                          </h2>
                          <div className="text-sm text-gray-700 space-y-1">
                            <p>Base Amount (before discount): BDT {originalAmount.toFixed(2)}</p>
                            <p>
                              Annual Discount ({discountRate}%): −BDT {discountAmount.toFixed(2)}
                            </p>
                            <p>Promo Discount (0%): −BDT 0.00</p>
                            <p className="font-semibold text-indigo-700 mt-2">
                              Total Amount (excluding VAT & AIT): BDT {baseAmount.toFixed(2)}
                            </p>
                          </div>
                        </div>

                        <div className="mb-6 text-sm text-gray-600">
                          <p>{entry.billingCycle === 'annual' ? 'Yearly discount applied' : ''}</p>
                          <p className="mt-2 italic">Thank you for your subscription.</p>
                        </div>
                      </div>

                      <button
                        className="px-4 py-1 bg-indigo-600 text-white rounded shadow hover:bg-indigo-700 text-xs"
                        onClick={() => {
                          sessionStorage.setItem(
                            'invoicePreviewData',
                            JSON.stringify({ email: entry.email, invoiceNo: entry.invoiceNo })
                          );
                          window.open('/invoice-preview', '_blank');
                        }}
                      >
                        Print Invoice
                      </button>
                      <button
                        className="px-4 py-1 bg-green-600 text-white rounded shadow hover:bg-green-700 text-xs"
                        onClick={() => handleDownloadPDF(entry, index)}
                      >
                        Download PDF
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
